import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { showToast } from '../components/Toast';
import TaskSubNav from '../components/TaskSubNav';
import { DSPageHeader, DSButton, DSIcon } from '../design-system/primitives';
import './taskManagementShared.css';

function TaskNeedsAttention() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getTasksNeedsAttention();
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to load needs-attention view');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleReopen = async (taskId) => {
    try {
      await apiService.reopenTask(taskId);
      await loadData();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to reopen task', 'error');
    }
  };

  const handleDismiss = async (taskId) => {
    try {
      await apiService.dismissTask(taskId);
      await loadData();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to dismiss task', 'error');
    }
  };

  if (loading) {
    return <div className="loading-inline">Loading...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  const totalCount = (data?.suggestionsPendingReview?.length || 0)
    + (data?.completionsAwaitingBountyConfirmation?.length || 0)
    + (data?.recentlyAutoClosed?.length || 0);

  const hubLinks = (
    <div className="tm-hub-links">
      <DSButton to="/tasks/templates" appearance="outline" tone="secondary" size="sm" leadingIcon={<DSIcon name="clipboard" />}>Template Library</DSButton>
      <DSButton to="/tasks/walkthrough" appearance="outline" tone="secondary" size="sm" leadingIcon={<DSIcon name="wrench" />}>Maintenance Walkthrough</DSButton>
      <DSButton to="/tasks/leaderboard" appearance="outline" tone="secondary" size="sm" leadingIcon={<DSIcon name="trophy" />}>Task Leaderboard</DSButton>
      <DSButton to="/tasks/reporting" appearance="outline" tone="secondary" size="sm" leadingIcon={<DSIcon name="target" />}>Task Reporting</DSButton>
    </div>
  );

  if (totalCount === 0) {
    return (
      <div className="tm-page">
        <DSPageHeader title="Manage" />
        {hubLinks}
        <div className="tm-empty-state">
          <h2>You're all caught up! 🎉</h2>
          <p>No pending suggestions, bounty confirmations, or auto-closed tasks need your attention right now.</p>
        </div>
        <TaskSubNav />
      </div>
    );
  }

  return (
    <div className="tm-page">
      <DSPageHeader title="Manage" />
      {hubLinks}

      <h2 className="tm-manage-section-heading"><DSIcon name="bell" /> Needs Attention</h2>

      {data.suggestionsPendingReview.length > 0 && (
        <div className="tm-section">
          <h2><DSIcon name="file" /> Suggestions Pending Review ({data.suggestionsPendingReview.length})</h2>
          <div className="tm-card-list">
            {data.suggestionsPendingReview.map((s) => (
              <div key={s.id} className="tm-needs-attention-row">
                <div>
                  <strong>{s.title}</strong>
                  <div className="tm-suggestion-meta">by {s.suggested_by_name} · {new Date(s.created_at).toLocaleDateString()}</div>
                </div>
                <DSButton to={`/tasks/suggestions/${s.id}/review`} tone="primary" appearance="solid" size="sm">Review</DSButton>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.completionsAwaitingBountyConfirmation.length > 0 && (
        <div className="tm-section">
          <h2><DSIcon name="trophy" /> Completions Awaiting Bounty Confirmation ({data.completionsAwaitingBountyConfirmation.length})</h2>
          <div className="tm-card-list">
            {data.completionsAwaitingBountyConfirmation.map((t) => (
              <div key={t.id} className="tm-needs-attention-row">
                <div>
                  <strong>{t.title}</strong>
                  <div className="tm-suggestion-meta">${t.bounty_amount} bounty · {t.point_value} pts</div>
                </div>
                <DSButton to={`/tasks/${t.id}/confirm-bounty`} tone="primary" appearance="solid" size="sm">Confirm</DSButton>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.recentlyAutoClosed.length > 0 && (
        <div className="tm-section">
          <h2><DSIcon name="clock" /> Recently Auto-Closed ({data.recentlyAutoClosed.length})</h2>
          <div className="tm-card-list">
            {data.recentlyAutoClosed.map((t) => (
              <div key={t.id} className="tm-needs-attention-row">
                <div>
                  <strong>{t.title}</strong>
                  <div className="tm-suggestion-meta">Auto-closed {t.auto_closed_at ? new Date(t.auto_closed_at).toLocaleDateString() : ''}</div>
                </div>
                <div className="tm-actions">
                  <DSButton appearance="solid" tone="primary" size="sm" onClick={() => handleReopen(t.id)}>Reopen</DSButton>
                  <DSButton appearance="outline" tone="secondary" size="sm" onClick={() => handleDismiss(t.id)}>Dismiss</DSButton>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <TaskSubNav />
    </div>
  );
}

export default TaskNeedsAttention;
